// @flow
import get from 'lodash/fp/get';
import sum from 'lodash/fp/sum';
import find from 'lodash/fp/find';
import pipe from 'lodash/fp/pipe';
import reduce from 'lodash/fp/reduce';
import sortBy from 'lodash/fp/sortBy';
import type { _Iteratee } from 'lodash';
import flatMap from 'lodash/fp/flatMap';
import map from 'lodash/fp/map';
import { adjustHue } from 'polished';
import { identity, reverse } from 'lodash/fp';

import { shallowTagValueStrings } from './tag-tree';
import { getField } from './model';
import type { SearchableVariationPair } from './product';
import {
  variationPairCompoundKey,
  type Product,
  type Variation,
  type StockItem,
  type VariationPriceRenderer,
  type VariationPair,
  variationKey,
  sizeName,
  minimumPurchase,
  stockItemCompoundKey,
  stockItemComponents,
  isComponentsInventoryMode,
  unitsPerBundle,
} from './product';

import { sortByDirection } from 'modules/sorting';
import {
  multPrice,
  sumPrices,
  resolvedPriceForComparison,
  resolvedPriceForComparisonWithDefaulting,
} from 'modules/price';
import type { PriceGroupToMonetaryAmountMap } from 'modules/monetary-amount';
import {
  type AllocationRecord,
  type WarehouseId,
  DEFAULT_WAREHOUSE_ID,
} from 'modules/inventory';
import { toDate } from 'modules/date';
import { fromPairs } from 'modules/object';
import {
  wholesalePriceGroup,
  retailPriceGroup,
  type Customer,
} from 'modules/customer';
import type { Upcharge } from 'modules/upcharge';
import type { PageProductEmbellishments } from 'modules/product-embellishment-provider';

/**
 * The maximum quantity that can ever be selected for a stockitem.
 *
 * @constant
 * @type {number}
 * @default
 * @todo Do we actually want this?
 */
export const ABSOLUTE_MAX_SELECTABLE_QUANTITIES: 9999 = 9999;

// a QuantitySource is a quantity-source pair
export type QuantitySource = $ReadOnly<{| source: string, quantity: number |}>;
export type Upcharges = $ReadOnlyArray<Upcharge>;

export type PageItemFreightDiscount = $ReadOnly<{|
  sales_program: string,
  ship_method_name: string,
  ship_method_code: string,
|}>;

export type PageItemShipping = $ReadOnly<{|
  freight_discount?: PageItemFreightDiscount,
|}>;

export type PageItem = {
  uniqueId: string,
  stockItem: StockItem,
  quantity: number,
  variation?: Variation,
  quantitySource?: $ReadOnlyArray<QuantitySource>,
  discounts: { [programName: string]: number },
  upcharges?: Upcharges,
  referenceQuantity?: number | null,
  shipping?: PageItemShipping,
};

export type PageProduct = {
  pageItems: $ReadOnlyArray<PageItem>,
  product: Product,
  variation: Variation,
  arriveOn: string,
  totalQuantity: number,
  totalPrices: PriceGroupToMonetaryAmountMap,
  embellishments?: PageProductEmbellishments,
  coordinationGroup?: number,
  arriveOn: Date | string,
  position: number,
  // Boxes: positive integer > 0, representing
  // # of boxes items will be shipped in
  boxes?: number,
  readOnly?: boolean,
};

export type SortMethod =
  | 'manual' // 'custom' in Scramble4, conveniently handled as a default case here
  | 'catalog'
  | 'name'
  | 'price'
  | 'number';

export const pageItemStockItem = (pageItem: PageItem): StockItem =>
  getField(pageItem, 'stockItem');

export const sortedPageItems = (
  list: $ReadOnlyArray<PageItem>,
): $ReadOnlyArray<PageItem> =>
  sortBy(([(si) => si.stockItem.position]: _Iteratee<PageItem>[]))(list);

export const pageProductPageItems = (
  pageProduct: PageProduct,
): $ReadOnlyArray<PageItem> =>
  sortedPageItems(getField(pageProduct, 'pageItems'));

export const pageProductsInProductNameOrder = (
  pageProducts: $ReadOnlyArray<PageProduct>,
  descending: boolean = false,
): $ReadOnlyArray<PageProduct> =>
  sortByDirection(pageProducts, descending, (pp) => pp.product.name);

export const pageProductsInProductPositionOrder = (
  pageProducts: $ReadOnlyArray<PageProduct>,
  descending: boolean = false,
): $ReadOnlyArray<PageProduct> =>
  sortByDirection(pageProducts, descending, (pp) => pp.product.position);

export const pageProductsInPriceOrder = (
  pageProducts: $ReadOnlyArray<PageProduct>,
  customer?: Customer,
  descending: boolean = false,
): $ReadOnlyArray<PageProduct> =>
  sortByDirection(pageProducts, descending, (pp) =>
    customer
      ? resolvedPriceForComparison({
          price: pp.product.price_range[0],
          wholesalePriceGroup: wholesalePriceGroup(customer),
          retailPriceGroup: retailPriceGroup(customer),
        })
      : resolvedPriceForComparisonWithDefaulting({
          price: pp.product.price_range[0],
        }),
  );

export const pageProductsInProductNumberOrder = (
  pageProducts: $ReadOnlyArray<PageProduct>,
  descending: boolean = false,
): $ReadOnlyArray<PageProduct> =>
  sortByDirection(pageProducts, descending, (pp) => pp.product.number);

export const inPositionOrder: (
  descending: boolean,
) => ($ReadOnlyArray<PageProduct>) => $ReadOnlyArray<PageProduct> =
  (descending) => (pageProducts) =>
    pipe(
      sortBy((pageProduct) => pageProduct.position),
      descending ? reverse : identity,
    )(pageProducts);

export const sorted = (
  pageProducts: $ReadOnlyArray<PageProduct>,
  sort?: SortMethod = 'manual',
  customer?: Customer,
  descending?: boolean = false,
): $ReadOnlyArray<PageProduct> => {
  // no do-expressions in flow :(
  switch (sort) {
    case 'number':
      return pageProductsInProductNumberOrder(pageProducts, descending);
    case 'catalog':
      return pageProductsInProductPositionOrder(pageProducts, descending);
    case 'price':
      return pageProductsInPriceOrder(pageProducts, customer, descending);
    case 'name':
      return pageProductsInProductNameOrder(pageProducts, descending);
    case 'manual':
    default:
      return inPositionOrder(descending)(pageProducts);
  }
};

export const pageProductStockItems = (
  pageProduct: PageProduct,
): Array<StockItem> => pageProductPageItems(pageProduct).map(pageItemStockItem);

export const pageItemQuantity = (pageItem: PageItem): number =>
  getField(pageItem, 'quantity');

export const stockItemsToQuantities = (
  pageProduct: PageProduct,
): Array<[StockItem, number]> =>
  pageProductPageItems(pageProduct).map((pi) => [
    pageItemStockItem(pi),
    pageItemQuantity(pi),
  ]);

export const sizesToQuantities = (
  pageProduct: PageProduct,
  separator: string,
): Array<[string, number]> =>
  stockItemsToQuantities(pageProduct).map(([stockItem, q]) => [
    sizeName({ stockItem, separator }),
    q,
  ]);

export const sizesToQuantitiesWithoutZeroes = (
  pageProduct: PageProduct,
  separator: string,
): Array<[string, number]> =>
  sizesToQuantities(pageProduct, separator).filter((_, q) => !!q);

export const pageProductQuantity = (pageProduct: PageProduct): number =>
  sum(pageProductPageItems(pageProduct).map(pageItemQuantity)) || 0;

export const pageItemPrices = (
  pageItem: PageItem,
): PriceGroupToMonetaryAmountMap =>
  getField(pageItemStockItem(pageItem), 'prices') || { ...null };

export const pageItemTotalPrices = (
  pageItem: PageItem,
): PriceGroupToMonetaryAmountMap =>
  multPrice({
    price: pageItemPrices(pageItem),
    factor: pageItemQuantity(pageItem),
  }) || { ...null };

export const pageProductTotalPrices = (
  pageProduct: PageProduct,
): PriceGroupToMonetaryAmountMap =>
  sumPrices(pageProductPageItems(pageProduct).map(pageItemTotalPrices)) || {
    ...null,
  };

export const pageProductVariation = (pageProduct: PageProduct): Variation =>
  getField(pageProduct, 'variation');

/**
 * Get the quantities of a pageItem for a given source.
 *
 * @param {PageItem} pageItem
 * @param {string | null} source
 * @returns {number}
 */
export const pageItemQuantityGivenSource = (
  pageItem: PageItem,
  source: ?string,
): number =>
  (pageItem.quantitySource || []).length === 0
    ? pageItem.quantity
    : pipe(
        find((qs) => qs.source === source),
        get('quantity'),
      )(pageItem.quantitySource || { ...null }) || 0;

export const quantityForPageItemGivenSource = pageItemQuantityGivenSource;

/**
 * Get the quantity of a pageProduct for a given source.
 * (A PageProduct has multiple pageItems)
 *
 * @param {PageProduct} pageProduct
 * @param {string} source
 * @returns {number}
 */
export const pageProductQuantityGivenSource = (
  pageProduct: PageProduct,
  source: string,
): number =>
  reduce(
    (acc, pi) => acc + pageItemQuantityGivenSource(pi, source),
    0,
  )(getField(pageProduct, 'pageItems'));

export const pageProductQuantitiesGivenSource =
  (source: string): ((pageProduct: PageProduct) => { +[string]: number }) =>
  (pageProduct: PageProduct): { +[string]: number } =>
    fromPairs(
      pageProduct.pageItems.map((pageItem) => [
        pageItem.stockItem.key,
        pageItemQuantityGivenSource(pageItem, source),
      ]),
    );

export const quantityForPageProductGivenSource = pageProductQuantityGivenSource;

const pageItemTotalPricesGivenSource = (
  pageItem: PageItem,
  source: ?string,
): PriceGroupToMonetaryAmountMap =>
  multPrice({
    price: pageItemPrices(pageItem),
    factor: pageItemQuantityGivenSource(pageItem, source),
  }) || { ...null };

export const pageProductTotalPricesGivenSource = (
  pageProduct: PageProduct,
  source: ?string,
): PriceGroupToMonetaryAmountMap =>
  sumPrices(
    pageProductPageItems(pageProduct).map((pi) =>
      pageItemTotalPricesGivenSource(pi, source),
    ),
  ) || { ...null };

export const pageItemByUpc = (
  pageProduct: PageProduct,
  upc: string,
): PageItem | null =>
  pageProductPageItems(pageProduct).find(
    (pi) => String(pi.stockItem.upc) === String(upc),
  ) || null;

export const pageProductByUpc = (
  list: Array<PageProduct>,
  upc: string,
): PageProduct | null => list.find((pp) => pageItemByUpc(pp, upc)) || null;

export const pageItemBySku = (
  pageProduct: PageProduct,
  sku: string,
): PageItem | null =>
  pageProductPageItems(pageProduct).find(
    (pi) => String(pi.stockItem.sku) === String(sku),
  ) || null;

export const pageProductBySku = (
  list: Array<PageProduct>,
  sku: string,
): PageProduct | null => list.find((pp) => pageItemBySku(pp, sku)) || null;

export const pageItemByKey = (
  pageProduct: PageProduct,
  key: string,
): PageItem | null =>
  pageProductPageItems(pageProduct).find(
    (pi) => String(pi.stockItem.key) === String(key),
  ) || null;

export const pageProductByStockItemKey = (
  list: Array<PageProduct>,
  key: string,
): PageProduct | null => list.find((pp) => pageItemByKey(pp, key)) || null;

/**
 * Get the pageProduct for a given sku or upc, for the case where we
 * don't know if the key we've been given is a UPC or a SKU:
 * https://github.com/ElasticSuite/scramble4/pull/6352#issuecomment-420635703
 */
export const pageProductBySkuOrUpcOrKey = (
  list: Array<PageProduct>,
  skuOrUpc: string,
): PageProduct | null =>
  pageProductBySku(list, skuOrUpc) ||
  pageProductByUpc(list, skuOrUpc) ||
  pageProductByStockItemKey(list, skuOrUpc);

/**
 * Get the pageItem for a given sku or upc, for the case where we
 * don't know if the key we've been given is a UPC or a SKU:
 * https://github.com/ElasticSuite/scramble4/pull/6352#issuecomment-420635703
 */
export const pageItemBySkuOrUpcOrKey = (
  pageProduct: PageProduct,
  skuOrUpc: string,
): PageItem | null =>
  pageItemBySku(pageProduct, skuOrUpc) ||
  pageItemByUpc(pageProduct, skuOrUpc) ||
  pageItemByKey(pageProduct, skuOrUpc);

export const pageItemUsesQuantitySource = (pageItem: PageItem): boolean =>
  Boolean(pageItem.quantitySource && pageItem.quantitySource.length > 0);

export const labelValueForKey = ({
  pageProduct,
  key,
  priceFinder,
}: {
  pageProduct: PageProduct,
  key: string,
  priceFinder: VariationPriceRenderer,
}): string =>
  ((
    {
      // before i was doing this with strings and lodash/fp/get, but i think you
      // lose type info that way
      product_name: (pp: PageProduct) => [pp.product.name],
      product_number: (pp: PageProduct) => [pp.product.number],
      color_name: (pp: PageProduct) => [pp.variation.name],
      color_code: (pp: PageProduct) => [pp.variation.code],
      price: (pp: PageProduct) => [priceFinder(pp.variation)],
    }[key] || ((_) => [])
  )(pageProduct).join(', '));

/**
 * Get a list of label values.
 *
 * Currently only used for whiteboard.
 */
export const labels = ({
  pageProduct,
  selectedLabels,
  allowedBaseLabels,
  tagLabels,
  priceFinder,
}: {
  pageProduct: PageProduct,
  selectedLabels: string[],
  tagLabels: string[],
  allowedBaseLabels: string[],
  priceFinder: VariationPriceRenderer,
}): string[] => [
  ...allowedBaseLabels
    .filter((key) => selectedLabels.includes(key))
    .map((key) =>
      labelValueForKey({
        pageProduct,
        key,
        priceFinder,
      }),
    ),
  ...tagLabels
    .filter((key) => selectedLabels.includes(key))
    .map((key) =>
      (
        [
          shallowTagValueStrings(key, pageProduct.variation.tags),
          shallowTagValueStrings(key, pageProduct.product.tags),
        ].find((list) => list.length > 0) || ['']
      ).join(', '),
    ),
];

export const byProductNumberAndVariationCode = (
  pageProducts: PageProduct[],
  productNumber: string,
  variationCode: string,
): PageProduct | null =>
  pageProducts.find(
    ({ variation, product }) =>
      variation.code === variationCode && product.number === productNumber,
  ) || null;

export const byVariationPair = (
  pageProducts: PageProduct[],
  [vpProduct, vpVariation]: VariationPair,
): PageProduct | null =>
  byProductNumberAndVariationCode(
    pageProducts,
    vpProduct.number,
    vpVariation.code,
  );

const pageItemTotalUpcharges =
  (priceGroups: { wholesalePriceGroup: string, retailPriceGroup: string }) =>
  (pageItem: PageItem): PriceGroupToMonetaryAmountMap =>
    pipe(
      map((u) => Number(u.amount)),
      sum,
      (num) => ({
        [priceGroups.wholesalePriceGroup]: num,
        [priceGroups.retailPriceGroup]: num,
      }),
    )(pageItem.upcharges || []);

export const pageProductTotalUpcharges = (
  pageProduct: PageProduct,
  priceGroups: {
    wholesalePriceGroup: string,
    retailPriceGroup: string,
  },
): PriceGroupToMonetaryAmountMap =>
  pipe(
    pageProductPageItems,
    map(pageItemTotalUpcharges(priceGroups)),
    sumPrices,
  )(pageProduct);

export const pageProductUpcharges = (pageProduct: PageProduct): Upcharges =>
  pipe(
    pageProductPageItems,
    flatMap((pi: PageItem): Upcharges => pi.upcharges || []),
  )(pageProduct).filter(Boolean);

export const mimimumPurchaseGivenPageProduct = (
  pageProduct: PageProduct,
): number => minimumPurchase((getField(pageProduct, 'product'): Product));

export const key = (pageProduct: PageProduct): string =>
  variationKey({
    product: pageProduct.product,
    variation: pageProduct.variation,
  });

export const byKey = (
  needle: string,
  haystack: $ReadOnlyArray<PageProduct>,
): PageProduct | null => haystack.find((pp) => key(pp) === needle) || null;

export const pageItemBySizeName =
  (
    name: string,
    altName: string | null,
  ): ((pageProduct: PageProduct) => PageItem | null) =>
  (pageProduct: PageProduct): PageItem | null =>
    pageProductPageItems(pageProduct).find(
      (pi) => pi.stockItem.name === name && pi.stockItem.alt_name === altName,
    ) || null;

export const coordinationGroupColor = (group: ?number): ?string =>
  typeof group === 'number' ? adjustHue((group - 1) * 65, 'red') : null;

export const allocations = (
  { product, arriveOn }: PageProduct,
  pageItem: PageItem,
  defaultWarehouseId?: WarehouseId | null,
): $ReadOnlyArray<AllocationRecord> => {
  const components = isComponentsInventoryMode(pageItem.stockItem)
    ? stockItemComponents(pageItem.stockItem)
    : [];

  const quantitySourceRecords = ((pageItem.quantitySource?.length || 0) > 0 &&
    pageItem.quantitySource) || [
    {
      quantity: pageItem.quantity,
      source: defaultWarehouseId || DEFAULT_WAREHOUSE_ID,
    },
  ];

  const componentAllocations =
    components && components.length > 0
      ? components.flatMap((component) =>
          quantitySourceRecords.map(({ quantity, source }) => ({
            component,
            quantity: unitsPerBundle(component) * quantity,
            warehouseId: source,
          })),
        )
      : quantitySourceRecords.map(({ quantity, source }) => ({
          component: pageItem.stockItem,
          quantity,
          warehouseId: source,
        }));

  const shipOn = toDate(arriveOn);

  return componentAllocations.map(({ component, quantity, warehouseId }) => ({
    stockItemKey: stockItemCompoundKey(product, component),
    quantity,
    warehouseId,
    shipOn,
    pageItemId: pageItem.uniqueId,
  }));
};

export const asSearchable = ({
  product,
  variation,
}: PageProduct): SearchableVariationPair => ({
  id: variationPairCompoundKey([product, variation]),
  productName: product.name,
  productNumber: product.number,
  variationCode: variation.code,
  variationName: variation.name,
});

export const canEmbellish = (pageProduct: PageProduct): boolean =>
  pageProductVariation(pageProduct).can_embellish || false;

export const embellishmentPayload = (
  pageProduct: PageProduct,
): PageProductEmbellishments | null => pageProduct.embellishments || null;
